import Grid from '@mui/material/Grid';
import MuiTextField from '@mui/material/TextField';
import React, { useEffect, useState } from 'react';
import { Field, useFormState } from 'react-final-form';

import Button from '@/components/Button';
import ConsultationDates from '@/components/ConsultationDates';
import DurationPicker from '@/components/DurationPicker';
import { useRateDisplay } from '@/hooks/useRateDisplay';
import { RateDisplayType } from '@/utils/rateDisplay';

import { RATE_UNAVAILABLE_SUBMIT_MESSAGE } from '../constants';
import { useStyles } from '../styles';
import type { Expert } from '../types';
import { getFirstName } from '../utils';

const MIN_DESCRIPTION_LENGTH = 100;

interface SchedulingStepProps {
  expert: Expert;
  viewer: {
    timezone: string;
  };
  onContinue: (event?: React.SyntheticEvent) => void;
  onCancel: () => void;
  valid?: boolean;
}

export function SchedulingStep({
  expert,
  viewer,
  onContinue,
  onCancel,
  valid = true,
}: SchedulingStepProps): JSX.Element {
  const s = useStyles();
  const displayType = useRateDisplay();
  const showUSD = displayType === RateDisplayType.USD;
  const { values } = useFormState({ subscription: { values: true } });
  const [charCount, setCharCount] = useState(0);

  // Initialize char count from form values (for when navigating back)
  useEffect(() => {
    const initialLength = values?.description?.trim()?.length || 0;
    if (initialLength > 0) {
      setCharCount(initialLength);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const firstName = getFirstName(expert.name);

  // A null rate means the API could not price this expert, so the booking will be refused
  // server-side. Stop here rather than at the payment step, so the user isn't asked for card
  // details for a request that cannot succeed (ENG-5571).
  //
  // Gate on clientRate whatever currency is displayed: continuing leads to completeSMBSignup,
  // which creates a USD/SMB account, so the booking is priced against client_rate either way.
  // Reading the displayed rate instead would let a null client_rate through under CREDITS
  // display, and would falsely block an anonymous viewer, whose credit_rate is null purely for
  // lack of `view_credit_rate`.
  const rateUnavailable = expert.clientRate === null || expert.clientRate === undefined;

  // Character count for visual feedback
  const hasMinChars = charCount >= MIN_DESCRIPTION_LENGTH;
  const charsRemaining = MIN_DESCRIPTION_LENGTH - charCount;

  return (
    <>
      <div className={s.schedulingContent}>
        <Grid container justifyContent="space-between">
          {/* Note to expert */}
          <Grid item xs={12}>
            <Field name="description">
              {({ input, meta: _meta }) => (
                <MuiTextField
                  {...input}
                  multiline
                  fullWidth
                  label={`Include a note to ${firstName}`}
                  onChange={(e) => {
                    input.onChange(e);
                    setCharCount(e.target.value.trim().length);
                  }}
                  helperText={
                    !hasMinChars
                      ? `Minimum length: ${charsRemaining} more character${charsRemaining !== 1 ? 's' : ''}`
                      : undefined
                  }
                  FormHelperTextProps={{
                    style: { color: charCount > 0 && !hasMinChars ? '#d32f2f' : undefined },
                  }}
                  InputLabelProps={{
                    style: { fontSize: 16 },
                  }}
                  InputProps={{
                    style: { paddingTop: 16 },
                  }}
                />
              )}
            </Field>
          </Grid>

          {/* Duration picker */}
          <Grid item xs={12} style={{ marginTop: 12 }}>
            <Field
              component={DurationPicker}
              name="duration"
              hourlyRate={showUSD ? (expert.clientRate ?? null) : undefined}
              showDollars={showUSD}
              creditRate={!showUSD ? expert.creditRate : undefined}
              showCredits={!showUSD}
              labelProps={{ style: { fontSize: 16, marginBottom: 18 } }}
            />
          </Grid>

          {/* Consultation dates */}
          <Grid item xs={12}>
            <ConsultationDates
              name="dates"
              viewer={viewer}
              userName={firstName}
              userTimezone={expert.timezone}
              label="Suggest times that work for you"
              style={{ marginTop: 12 }}
              hideTimezoneLink
            />
          </Grid>
        </Grid>
        {/* Both sibling steps explain the block rather than just disabling. Users can arrive
            here with a price already quoted on the profile CTA, so a silent disabled button
            reads as a broken form (ENG-5571). */}
        {rateUnavailable && (
          <div className={s.paymentError}>
            <p className={s.paymentErrorText}>{RATE_UNAVAILABLE_SUBMIT_MESSAGE}</p>
          </div>
        )}
      </div>

      {/* Footer with Cancel / Continue buttons */}
      <div className={s.footer}>
        <Button variant="text" size="normal" onClick={onCancel} classes={{ root: s.backButton }}>
          Cancel
        </Button>
        <Button
          color="teal"
          size="normal"
          onClick={onContinue}
          disabled={!valid || rateUnavailable}
          classes={{ root: s.continueButton }}
        >
          Continue
        </Button>
      </div>
    </>
  );
}

export default SchedulingStep;
